import { useEffect, useRef } from "react";
import { useResonanceLayout } from "../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { pageBackgroundOf } from "../theme/pageStyle";

/**
 * Applies an authored per-content page background (contents.page_style) to the page
 * frame, and restores the default when the page unmounts or the style disappears.
 *
 * Call it with the fetched content's page_style — it no-ops until the value arrives,
 * so it is safe to call on every render of a content page. Pages that set their own
 * backgroundColor on mount (e.g. DisplayNotIncluded) keep it unless the content
 * actually carries a page_style: the authored style wins.
 *
 * `resolve` turns the style into a background (default: pageBackgroundOf; in-app
 * reading pages pass readingPageBackgroundOf so an unstyled article keeps the tint).
 *
 * The restore only undoes THIS page's background: Ionic unmounts a page after the
 * page it returns to has already entered and set its own background (Resources list
 * ← article), and resetting then would wipe that page's background.
 */
export const usePageBackground = (
  pageStyleJson?: string | null,
  resolve: (pageStyleJson: string) => string = pageBackgroundOf
): void => {
  const { backgroundColor, setResonanceLayout } = useResonanceLayout();
  const currentBackground = useRef(backgroundColor);
  currentBackground.current = backgroundColor;

  useEffect(() => {
    if (!pageStyleJson) return;
    const applied = resolve(pageStyleJson);
    setResonanceLayout({ backgroundColor: applied });
    return () => {
      // "white" is the documented context default (ResonanceLayoutDefaultStateValue).
      if (currentBackground.current === applied) {
        setResonanceLayout({ backgroundColor: "white" });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageStyleJson]);
};
